// src/components/manager/shared/PostsSectionForm.jsx
import React, { useState, useEffect } from 'react';
import { Plus, Filter, TrendingUp, Users, Globe, Lock } from 'lucide-react';
import { PostComposer } from './PostComposer';
import { PostsFeed } from './PostsFeed';
import { postsService } from '../../../services/postsService';

export const PostsSectionForm = ({ 
  user,
  bookId, 
  templateType = 'organisation', 
  data = {}, 
  onChange, 
  errors = {} 
}) => {
  const [posts, setPosts] = useState([]);
  const [showComposer, setShowComposer] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadPosts();
  }, [bookId]);

  const loadPosts = async () => {
    try {
      setLoading(true);
      const postsData = await postsService.getPosts(bookId);
      setPosts(postsData);
      onChange('posts', postsData);
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePost = () => {
    setEditingPost(null);
    setShowComposer(true);
  };

  const handleEditPost = (post) => {
    setEditingPost(post);
    setShowComposer(true);
  };

  const handleSavePost = async (postData) => {
    try {
      if (editingPost) {
        await postsService.updatePost(bookId, editingPost.id, postData);
      } else {
        await postsService.createPost(bookId, {
          ...postData,
          
        });
      }
      await loadPosts();
      setShowComposer(false);
      setEditingPost(null);
    } catch (error) {
      console.error('Error saving post:', error);
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Delete this post? This action cannot be undone.')) return;
    
    try {
      await postsService.deletePost(bookId, postId);
      await loadPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const handleStatusChange = async (postId, newStatus) => {
    try {
      await postsService.updatePostStatus(bookId, postId, newStatus);
      await loadPosts();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getFilteredPosts = () => {
    switch (filter) {
      case 'published':
        return posts.filter(p => p.status === 'published');
      case 'drafts':
        return posts.filter(p => p.status === 'draft');
      case 'scheduled':
        return posts.filter(p => p.status === 'scheduled');
      default:
        return posts;
    }
  };

  const getStats = () => {
    const total = posts.length;
    const published = posts.filter(p => p.status === 'published').length;
    const drafts = posts.filter(p => p.status === 'draft').length;
    const totalViews = posts.reduce((sum, p) => sum + (p.views || 0), 0);
    const totalEngagement = posts.reduce((sum, p) => sum + (p.likes || 0) + (p.comments || 0), 0);

    return { total, published, drafts, totalViews, totalEngagement };
  };

  const stats = getStats();
  const filteredPosts = getFilteredPosts();

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-start">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">Community Posts</h2>
        <p className="text-gray-600">Share updates, news, and engage with your community</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-100 text-center">
          <div className="text-2xl font-bold text-blue-600">{stats.total}</div>
          <div className="text-sm text-gray-500">Total Posts</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-100 text-center">
          <div className="text-2xl font-bold text-green-600">{stats.published}</div>
          <div className="text-sm text-gray-500">Published</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-100 text-center">
          <div className="text-2xl font-bold text-gray-600">{stats.drafts}</div>
          <div className="text-sm text-gray-500">Drafts</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-100 text-center">
          <div className="text-2xl font-bold text-purple-600">{stats.totalViews}</div>
          <div className="text-sm text-gray-500">Total Views</div>
        </div>
        <div className="bg-white p-4 rounded-lg border border-gray-100 text-center">
          <div className="text-2xl font-bold text-indigo-600">{stats.totalEngagement}</div>
          <div className="text-sm text-gray-500">Engagement</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 justify-center">
        {[
          { key: 'all', label: 'All Posts', count: stats.total },
          { key: 'published', label: 'Published', count: stats.published },
          { key: 'drafts', label: 'Drafts', count: stats.drafts }
        ].map(({ key, label, count }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-4 py-2 rounded-full font-medium transition-colors ${
              filter === key
                ? 'bg-blue-600 text-white'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
            }`}
          >
            {label} {count > 0 && `(${count})`}
          </button>
        ))}
      </div>

      {/* Posts Feed */}
      <PostsFeed
        posts={filteredPosts}
        onCreatePost={handleCreatePost}
        onEditPost={handleEditPost}
        onDeletePost={handleDeletePost}
        onStatusChange={handleStatusChange}
        loading={loading}
      />

      {/* Post Composer Modal */}
      <PostComposer
        user={user} 
        isOpen={showComposer}
        onClose={() => {
          setShowComposer(false);
          setEditingPost(null);
        }}
        onSave={handleSavePost}
        initialPost={editingPost}
        templateType={templateType}
        bookId={bookId} // ✅ Pass bookId for file uploads
      />
    </div>
  );
};